import { useCallback, useRef } from 'react';

import {
  type ActiveFiltersState,
  type InsightItemTypes,
} from '../../../shared/types';
import { useLazyGetEntitlementInsightsQuery } from '../data-layer/entitlementTabApi';
import { type EntitlementSortState } from '../data-layer/entitlementTypes';
import { getEntitlementInsightsPayload } from '../utils';

const useGetInsights = (): {
  handleFetchInsights: (
    activeFilters: ActiveFiltersState[],
    searchKey: string | null,
    sortInAction: EntitlementSortState | null,
    activeInsight?: InsightItemTypes | null
  ) => Promise<InsightItemTypes[]>;
  isError: boolean;
  isLoading: boolean;
} => {
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [fetchInsights, { isError, isLoading }] =
    useLazyGetEntitlementInsightsQuery();

  const handleFetchInsights = useCallback(
    async (
      activeFilters: ActiveFiltersState[],
      searchKey: string | null,
      sortInAction: EntitlementSortState | null,
      activeInsight?: InsightItemTypes | null
    ): Promise<InsightItemTypes[]> => {
      abortRef.current?.('New Search');

      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const payload = getEntitlementInsightsPayload(
        activeFiltersMap,
        searchKey,
        sortInAction,
        activeInsight
      );

      try {
        const controller = fetchInsights(payload);

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch {
        throw Error('Failed fetching entitlement insights');
      }
    },
    [fetchInsights]
  );

  return { handleFetchInsights, isError, isLoading };
};

// eslint-disable-next-line import/prefer-default-export
export { useGetInsights };
